import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { fetchApi } from '../lib/apiClient';
// Explicitly import shared types, including ProcessStep
import { Conversation, Message, MessageContent, ProcessStep } from '../types';
import { getCurrentTimezoneOffset } from '../utils/getCurrentTimezoneOffset';

// 后端messageProcessLogs数据结构
interface MessageProcessLog {
  id: string;
  sequence: number;
  processType: string;
  processData: any;
  createdAt: string;
  targetAssistantMessageId: string;
  initiatingUserMessageId: string;
}

/**
 * 将后端返回的messageProcessLogs转换为前端的ProcessStep格式
 */
const transformProcessLogsToSteps = (logs: MessageProcessLog[]): ProcessStep[] => {
  if (!logs || logs.length === 0) return [];

  const processSteps: ProcessStep[] = [];

  for (const log of logs) {
    const { processType, processData, sequence, createdAt } = log;

    switch (processType) {
      case 'text':
        processSteps.push({
          type: 'text',
          text: processData.text || '',
          sequence,
          timestamp: createdAt,
        });
        break;

      case 'tool_result':
        processSteps.push({
          type: 'tool_result',
          toolId: log.id,
          toolName: processData.toolName || '',
          result: processData.data || processData.result,
          status: processData.status === 'error' ? 'error' : 'success',
          sequence,
          timestamp: createdAt,
        });
        break;

      case 'tool_error':
        processSteps.push({
          type: 'tool_error',
          toolId: log.id,
          toolName: processData.toolName,
          status: 'system_error', // 默认为系统错误
          result: {
            message: processData.error || processData.message || '工具执行失败',
            details: processData.details,
          },
          sequence,
          timestamp: createdAt,
        });
        break;

      default:
        // 其他类型作为文本处理
        processSteps.push({
          type: 'text',
          text: `[${processType}] ${JSON.stringify(processData)}`,
          sequence,
          timestamp: createdAt,
        });
        break;
    }
  }

  return processSteps.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};

/**
 * 聊天服务类
 * 对接后端 /chat/ 相关API
 */
export const chatV3Service = {
  /**
   * 获取对话列表
   * @param {number} limit - 返回的最大对话数量
   * @param {number} offset - 分页偏移量
   * @returns {Promise<{conversations: Conversation[], total: number, hasMore: boolean}>}
   */
  async getConversations(limit = 8, offset = 0): Promise<{ conversations: Conversation[], total: number, hasMore: boolean }> {
    const data = await fetchApi<{ conversations: Conversation[], total: number, hasMore: boolean }>(
      `/chat/conversations?limit=${limit}&offset=${offset}`,
      { method: 'GET' }
    );

    return {
      conversations: data.conversations,
      total: data.total,
      hasMore: data.hasMore
    };
  },

  /**
   * 获取单个对话详情及其最近消息
   * @param {string} conversationId - 对话ID
   * @returns {Promise<{conversation: Conversation, recentMessages: Message[], hasMoreMessages: boolean, totalMessages: number}>}
   */
  async getConversation(conversationId: string): Promise<{ conversation: Conversation, recentMessages: Message[], hasMoreMessages: boolean, totalMessages: number }> {
    const data = await fetchApi<{
      conversation: any,
      recentMessages: any[],
      hasMoreMessages: boolean,
      totalMessages: number
    }>(
      `/chat/conversation?conversationId=${conversationId}`,
      { method: 'GET' }
    );

    // Log the number of messages received before mapping
    console.log(`[chatV3Service.getConversation] Received ${data.recentMessages?.length || 0} messages for conversation ${conversationId} before mapping.`);

    // Backend returns message info with processLogs, map it to the full Message type
    const processedMessages: Message[] = (data.recentMessages || []).map((partialMsg: any) => {
      // 转换processLogs为ProcessStep格式
      const processLog = transformProcessLogsToSteps(partialMsg.processLogs || []);

      return {
        id: partialMsg.id,
        conversationId: conversationId,
        role: partialMsg.role,
        content: partialMsg.content,

        timestamp: partialMsg.timestamp,
        status: partialMsg.status || 'completed',
        parentMessageId: partialMsg.parentMessageId || null,
        metadata: partialMsg.metadata,

        // 将processLog提升为顶级字段
        processStep: processLog,
      } as Message;
    });

    return {
      conversation: data.conversation as Conversation,
      recentMessages: processedMessages, // Use the mapped full messages
      hasMoreMessages: data.hasMoreMessages,
      totalMessages: data.totalMessages
    };
  },

  /**
   * 加载更多历史消息
   * @param {string} conversationId - 对话ID
   * @param {string} [beforeId] - 消息ID，用于加载此ID之前的消息
   * @returns {Promise<{messages: Message[], hasMore: boolean, totalCount: number, oldestId: string|null, newestId: string|null}>}
   */
  async getMessages(conversationId: string, beforeId?: string): Promise<{ messages: Message[], hasMore: boolean, totalCount: number, oldestId: string | null, newestId: string | null }> {
    let url = `/chat/messages?conversationId=${conversationId}`;
    if (beforeId) url += `&beforeId=${beforeId}`;

    const data = await fetchApi<{
      messages: any[],
      hasMore: boolean,
      totalCount: number,
      oldestId: string | null,
      newestId: string | null
    }>(url, { method: 'GET' });

    // 转换消息格式，包含processLog
    const processedMessages: Message[] = (data.messages || []).map((msg: any) => {
      const processLog = transformProcessLogsToSteps(msg.processLogs || []);

      return {
        id: msg.id,
        conversationId: conversationId,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        status: msg.status,
        parentMessageId: msg.parentMessageId || null,
        characterId: msg.characterId,
        metadata: msg.metadata,
        // 将processLog提升为顶级字段
        processStep: processLog,
      } as Message;
    });

    return {
      messages: processedMessages,
      hasMore: data.hasMore,
      totalCount: data.totalCount,
      oldestId: data.oldestId,
      newestId: data.newestId
    };
  },

  /**
   * 发送消息 (统一处理创建和发送)
   * @param {string} text - 消息文本
   * @param {string} [conversationId] - 对话ID，不提供则创建新对话
   * @param {string[]} [mediaIds] - 媒体ID数组
   * @param {string} [characterId] - 角色卡ID
   * @returns {Promise<{userMessage: Message, aiMessage: Message, conversationId: string, isNewConversation: boolean}>} 使用导入的 Message 类型
   */
  async sendMessage(
    text: string,
    conversationId?: string,
    mediaIds?: string[],
    characterId?: string
  ): Promise<{ userMessage: Message, aiMessage: Message, conversationId: string, isNewConversation: boolean }> {
    const data = await fetchApi<{
      userMessage: Message,
      aiMessage: Message,
      conversation: { id: string, isNew: boolean }
    }>('/chat/send', {
      method: 'POST',
      body: {
        text,
        conversationId,
        mediaIds,
        characterId
      }
    });

    if (!data || !data.userMessage || !data.aiMessage || !data.conversation || !data.conversation.id) {
      throw new Error("Invalid response from server when sending message.");
    }

    return {
      userMessage: data.userMessage,
      aiMessage: data.aiMessage,
      conversationId: data.conversation.id,
      isNewConversation: data.conversation.isNew,
    };
  },

  /**
   * 删除对话
   * @param {string} conversationId - 要删除的对话ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteConversation(conversationId: string): Promise<{ success: boolean, message: string }> {
    return await fetchApi<{ success: boolean, message: string }>('/chat/conversation/delete', {
      method: 'POST',
      body: { conversationId }
    });
  },

  /**
   * 上传图片并获取媒体ID
   * @param {File} imageFile - 图片文件
   * @returns {Promise<string>} - 媒体ID
   */
  async uploadImage(imageFile: File): Promise<string> {
    const fileExt = imageFile.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `chat-media/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('media')
      .upload(filePath, imageFile);
    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage.from('media').getPublicUrl(filePath);
    const publicUrl = urlData.publicUrl;

    // Assuming backend Media type matches what we need for insert
    const { data: mediaData, error: mediaError } = await supabase
      .from('media')
      .insert({
        // Use correct snake_case column names for Supabase insert
        file_type: imageFile.type,
        url: publicUrl,
        storage_path: filePath,
        size: imageFile.size,
        status: 'pending' // Ensure status is set
      })
      .select('id')
      .single();

    if (mediaError) throw mediaError;
    const mediaId = mediaData.id;

    // Async media processing call
    fetchApi('/media/process', {
      method: 'POST',
      body: { mediaId: mediaId }
    }).then((data) => {
      console.log(`Media processing started for ${mediaId}:`, data);
    }).catch((error) => {
      console.error(`Error processing media ${mediaId}:`, error);
    });

    return mediaId;
  },

  /**
   * 发送流式聊天消息
   * @param {MessageContent[]} content - 消息内容
   * @param {string} [conversationId] - 对话ID，不提供则创建新对话
   * @param {string} [characterId] - 角色卡ID
   * @returns {Promise<Response>} - 返回fetch响应对象，用于手动处理SSE流
   */
  async sendStreamingMessage(
    content: MessageContent[],
    conversationId?: string,
    characterId?: string,
    userMessageId?: string,
    assistantMessageId?: string,
  ): Promise<Response> {
    // 构建请求体
    const requestBody: any = { content };
    if (conversationId) requestBody.conversationId = conversationId;
    if (characterId) requestBody.characterId = characterId;
    if (userMessageId) requestBody.userMessageId = userMessageId;
    if (assistantMessageId) requestBody.assistantMessageId = assistantMessageId;

    // 获取当前会话
    const session = await supabase.auth.getSession();
    const accessToken = session.data.session?.access_token;

    // 添加自定义请求头
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': accessToken ? `Bearer ${accessToken}` : '',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Accept': 'text/event-stream', // 明确指定接受SSE流
      'x-timezone': getCurrentTimezoneOffset().toString(),
    };

    // 构建API URL
    const baseApiUrl = import.meta.env.VITE_BACKEND_API_URL.replace(/\/$/, '');
    const apiUrl = `${baseApiUrl}/chat/stream/combined`;

    // 发送POST请求初始化流式聊天
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`初始化流式聊天失败: ${response.status} ${response.statusText}`);
    }

    return response;
  },


};
